import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { auth, db } from '@/api/firebase';
import { toast } from '@/components/ui/use-toast';
import { useMainContext } from '@/context/main-context';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';

export default function Attendance({ group, students }) {
  const { teacherData } = useMainContext();
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [changed, setChanged] = useState(false);
  const [saving, setSaving] = useState(false);

  console.log(teacherData);

  // Sana key (local)
  const getLocalDateKey = (date) => date.toLocaleDateString('en-CA');

  const generateDatesForMonth = (month, dayType) => {
    const dates = [];
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 0) continue;

      if (dayType === 'odd' && [1, 3, 5].includes(dayOfWeek)) {
        dates.push({
          date,
          formatted: date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
          }),
          weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
          key: getLocalDateKey(date),
        });
      } else if (dayType === 'even' && [2, 4, 6].includes(dayOfWeek)) {
        dates.push({
          date,
          formatted: date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
          }),
          weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
          key: getLocalDateKey(date),
        });
      }
    }
    return dates;
  };

  const currentDates = generateDatesForMonth(
    selectedMonth,
    group?.dayType || 'odd'
  );

  const today = new Date();
  const todayKey = getLocalDateKey(today);
  const todayDayOfWeek = today.getDay();
  const isTodayRelevant =
    selectedMonth.getMonth() === today.getMonth() &&
    selectedMonth.getFullYear() === today.getFullYear() &&
    ((group?.dayType === 'odd' && [1, 3, 5].includes(todayDayOfWeek)) ||
      (group?.dayType === 'even' && [2, 4, 6].includes(todayDayOfWeek)));

  const handleAttendanceToggle = (studentId, dateKey) => {
    setAttendanceData((prev) => {
      const dateData = prev[dateKey] || {};
      const currentStatus = dateData[studentId] ?? true;
      const newStatus = !currentStatus;
      const updatedDateData = { ...dateData };

      if (newStatus === true) {
        delete updatedDateData[studentId];
      } else {
        updatedDateData[studentId] = false;
      }

      const newData = { ...prev };
      if (Object.keys(updatedDateData).length === 0) {
        delete newData[dateKey];
      } else {
        newData[dateKey] = updatedDateData;
      }

      // Compare with original
      setChanged(JSON.stringify(newData) !== JSON.stringify(originalData));

      return newData;
    });
  };

  const handleSaveAttendance = async () => {
    if (!auth.currentUser) return;

    setSaving(true);
    const uid = teacherData?.role;
    const batchPromises = [];

    Object.entries(attendanceData).forEach(([dateKey, studentsStatus]) => {
      const ref = doc(
        db,
        'users',
        uid,
        'groups',
        group.id,
        'attendance',
        dateKey
      );

      batchPromises.push(setDoc(ref, studentsStatus, { merge: false }));
    });

    try {
      await Promise.all(batchPromises);
      toast({
        title: 'Muvaffaqiyatli saqlandi',
      });
      setOriginalData(attendanceData);
      setChanged(false);
    } catch (err) {
      console.error('Attendance saqlashda xatolik:', err);
      toast({
        title: 'Saqlashda xatolik',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const loadAttendance = async () => {
      const uid = teacherData?.role;
      if (!uid || !group?.id) return;

      try {
        const colRef = collection(
          db,
          'users',
          uid,
          'groups',
          group.id,
          'attendance'
        );

        // selectedMonth uchun oy boshlanishi va tugashini aniqlaymiz
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth();
        const monthStart = new Date(year, month, 1).toLocaleDateString('en-CA'); // YYYY-MM-DD
        const monthEnd = new Date(year, month + 1, 0).toLocaleDateString(
          'en-CA'
        );

        // Faqat shu oyning ma’lumotlarini olamiz
        const q = query(
          colRef,
          where('__name__', '>=', monthStart),
          where('__name__', '<=', monthEnd)
        );

        const snap = await getDocs(q);
        const data = {};
        snap.forEach((docSnap) => {
          data[docSnap.id] = docSnap.data();
        });

        setAttendanceData(data);
        setOriginalData(data);
        console.log(
          'Loaded attendance for month:',
          monthStart,
          '-',
          monthEnd,
          data
        );
      } catch (err) {
        console.error('❌ Error loading attendance:', err);
      }
    };

    loadAttendance();
  }, [teacherData?.role, group?.id, selectedMonth]);

  const handlePreviousMonth = () => {
    setSelectedMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setSelectedMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const getMonthYearDisplay = () => {
    return selectedMonth.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  if (!group || !students?.length) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No group or students data available
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <div className="pb-2 pt-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 p-1 rounded-lg bg-background border min-w-[150px] lg:min-w-[200px] truncate">
            <Button
              variant="secondary"
              size="sm"
              onClick={handlePreviousMonth}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium text-center mx-auto">
              {getMonthYearDisplay()}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleNextMonth}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={handleSaveAttendance} disabled={!changed || saving}>
            {saving ? 'Saqlanmoqda...' : 'Saqlash'}
          </Button>
        </div>
      </div>

      <div>
        {currentDates.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No {group.dayType} weekdays in this month
          </div>
        ) : (
          <div className="border rounded-lg">
            <div className="overflow-x-auto rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="sticky text-primary left-0 z-10 bg-muted border-r min-w-[150px] lg:min-w-[200px] truncate font-semibold">
                      O'quvchilar
                    </TableHead>
                    {currentDates.map((dateObj) => {
                      const isToday =
                        isTodayRelevant && dateObj.key === todayKey;
                      return (
                        <TableHead
                          key={dateObj.key}
                          className={`text-center min-w-[120px] border-r last:border-r-0 border-border bg-background transition-all duration-200 ${
                            isToday
                              ? 'bg-muted text-primary'
                              : 'text-muted-foreground'
                          }`}
                        >
                          <div className="text-sm font-medium">
                            {dateObj.formatted}
                          </div>
                          <div className="text-xs opacity-75 font-normal">
                            {dateObj.weekday}
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student, index) => (
                    <TableRow
                      key={student.id}
                      className={`hover:bg-muted/50 transition-colors ${
                        index % 2 === 0 ? 'bg-muted/20' : ''
                      }`}
                    >
                      <TableCell className="sticky left-0 z-10 bg-muted border-r font-medium">
                        <div className="truncate pr-2">{student.fullName}</div>
                      </TableCell>
                      {currentDates.map((dateObj) => {
                        const isPresent = !(
                          attendanceData[dateObj.key]?.[student.id] === false
                        );
                        const isToday =
                          isTodayRelevant && dateObj.key === todayKey;

                        return (
                          <TableCell
                            key={`${student.id}-${dateObj.key}`}
                            className={`text-center border-r last:border-0 p-2 bg-background transition-all duration-200 ${
                              isToday ? 'bg-muted/40' : ''
                            }`}
                          >
                            <Button
                              variant="default"
                              size="sm"
                              style={{
                                backgroundColor: isPresent
                                  ? '#059669'
                                  : '#fadcdcff',
                                color: isPresent ? '#ffffff' : '#e1311aff',
                                borderColor: isPresent
                                  ? '#bbfae6ff'
                                  : '#fecaca',
                              }}
                              className="w-full h-8"
                              onClick={() =>
                                handleAttendanceToggle(student.id, dateObj.key)
                              }
                            >
                              {isPresent ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
